import type { StageMaterial } from './stageDestruction'

export type CrossArenaAnchor={
 id:string
 source:'elyon'|'battle'
 name:string
 regionName:string
 biome:string
 landmarkAssetId:string
 discoveryRadius:number
 combatRadius:number
 lore:string
 materialBias:StageMaterial
 integrityBias:number
 accent:string
}

/**
 * Portable extraction from Elyon arena-world-fusion + Battle Arena Earth OS anchors.
 * This is intentionally renderer-free: Fight consumes it as combat/world metadata,
 * not as an Elyon dependency, so the fighting runtime remains sovereign.
 */
export const CROSS_WORLD_ARENA_ANCHORS:readonly CrossArenaAnchor[]=[
 {id:'ancient-ruins',source:'battle',name:'Ancient Ruins',regionName:'Machu Picchu',biome:'ancient-ruins',landmarkAssetId:'ruin-broken-pillar',discoveryRadius:55,combatRadius:18,lore:'A forgotten combat ground embedded in ancient ruins.',materialBias:'stone',integrityBias:1.12,accent:'#ffbc64'},
 {id:'crystal-cavern',source:'battle',name:'Crystal Cavern',regionName:'Alpine Caverns',biome:'crystal',landmarkAssetId:'crystal-spire-formation',discoveryRadius:60,combatRadius:18,lore:'A crystalline arena where resonance and impact reshape the battlefield.',materialBias:'crystal',integrityBias:1.18,accent:'#78f7ff'},
 {id:'forest-grove',source:'battle',name:'Forest Grove',regionName:'Temperate Forest',biome:'forest',landmarkAssetId:'grove-cave-ancient-stump',discoveryRadius:60,combatRadius:18,lore:'A living grove whose roots and trunks become part of the fight.',materialBias:'organic',integrityBias:.92,accent:'#62ffb1'},
 {id:'lava-chasm',source:'battle',name:'Lava Chasm',regionName:'Volcanic Region',biome:'volcanic',landmarkAssetId:'volcanic-rock-outcrop',discoveryRadius:65,combatRadius:20,lore:'A volcanic fracture where heat, pressure and destruction continuously interact.',materialBias:'stone',integrityBias:1.25,accent:'#ff6b42'},
 {id:'frozen-wastes',source:'battle',name:'Frozen Wastes',regionName:'Arctic Tundra',biome:'frozen',landmarkAssetId:'glacial-cliff-face',discoveryRadius:65,combatRadius:20,lore:'A frozen battlefield where cold and fracture become weapons.',materialBias:'crystal',integrityBias:.85,accent:'#a8e9ff'},
 {id:'sanctum',source:'battle',name:'Sanctum',regionName:'Urban Sanctuary',biome:'sanctum',landmarkAssetId:'building-temple-crystal',discoveryRadius:60,combatRadius:18,lore:'A protected civilization site that can be entered as world location and arena.',materialBias:'stone',integrityBias:1.08,accent:'#5bbcff'},
 {id:'void-rift',source:'battle',name:'Void Rift',regionName:'Mystical Nexus',biome:'void',landmarkAssetId:'void-shard-formation',discoveryRadius:70,combatRadius:20,lore:'A reality fracture where pressure, energy and spatial instability meet.',materialBias:'crystal',integrityBias:1.2,accent:'#b687ff'},
] as const

const MATERIAL_RULES:readonly [StageMaterial,RegExp][]=[
  ['crystal',/crystal|gem|shard|prism|obelisk|void/i],
 ['crystal',/ice|frost|frozen|glacier|glass|window/i],
 ['metal',/metal|steel|iron|bronze|machine|armor|gate|reactor/i],
 ['wood',/wood|tree|trunk|branch|wooden|timber|crate/i],
 ['stone',/stone|rock|boulder|pillar|wall|monolith|statue|temple|ruin|concrete|barrier/i],
 ['earth',/earth|cliff|soil|sand|terrain|moon/i],
 ['organic',/plant|flora|mushroom|root|vine|organic|grove/i],
]

export function inferCrossWorldStageMaterial(label:string,fallback:StageMaterial):StageMaterial{
 const source=String(label||'')
 for(const [material,rule] of MATERIAL_RULES) if(rule.test(source)) return material
 return fallback
}

export function crossWorldAnchorForArena(arenaId:string):CrossArenaAnchor|undefined{
 const key=arenaId.toLowerCase()
 if(key.includes('crystal')||key.includes('xyloria')) return CROSS_WORLD_ARENA_ANCHORS.find(a=>a.id==='crystal-cavern')
 if(key.includes('forest')||key.includes('verdant')) return CROSS_WORLD_ARENA_ANCHORS.find(a=>a.id==='forest-grove')
 if(key.includes('magma')||key.includes('lava')||key.includes('thermal')||key.includes('phoenix')) return CROSS_WORLD_ARENA_ANCHORS.find(a=>a.id==='lava-chasm')
 if(key.includes('frozen')||key.includes('ice')) return CROSS_WORLD_ARENA_ANCHORS.find(a=>a.id==='frozen-wastes')
 if(key.includes('void')||key.includes('abyss')||key.includes('rift')) return CROSS_WORLD_ARENA_ANCHORS.find(a=>a.id==='void-rift')
 if(key.includes('capital')||key.includes('sanctum')||key.includes('bastion')) return CROSS_WORLD_ARENA_ANCHORS.find(a=>a.id==='sanctum')
 return CROSS_WORLD_ARENA_ANCHORS.find(a=>a.id==='ancient-ruins')
}

export function applyCrossWorldStageTuning(arenaId:string,def:{id:string;label:string;material:StageMaterial;maxIntegrity:number;accent?:string}){
 const anchor=crossWorldAnchorForArena(arenaId)
 const material=inferCrossWorldStageMaterial(`${arenaId} ${def.id} ${def.label} ${anchor?.biome??''} ${anchor?.landmarkAssetId??''}`,def.material)
 const maxIntegrity=Math.max(1,Math.round(def.maxIntegrity*(anchor?.integrityBias??1)))
 return {...def,material,maxIntegrity,accent:def.accent??anchor?.accent}
}

export const CROSS_WORLD_FIGHT_CAPABILITIES=Object.freeze({
 arenaAnchors:CROSS_WORLD_ARENA_ANCHORS.length,
 materialInference:true,
 battleArenaEarthAnchors:true,
 elyonWorldFusionContract:true,
 rendererFree:true,
 fightRuntimeSovereign:true,
})
